import chroma from 'chroma-js';
import {css, getSimilarColor} from '../utils.js';

const styles = css`
    /* CSS for the header controls bar */

    .ddk-container .controls-header-container {
        height: 100%;
    }

    .ddk-container .controls-header-container.sticky {
        position: sticky;
        top: -1px;
        z-index: 10;
    }

    .ddk-container .global-controls-bar {
        position: relative;
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        transition: box-shadow 0.7s ease;
    }

    .ddk-container .global-controls-bar.stickied {
        box-shadow:
            0px 7px 16px 0px rgba(0, 0, 0, 0.04),
            0px 30px 30px 0px rgba(0, 0, 0, 0.04),
            0px 67px 40px 0px rgba(0, 0, 0, 0.02),
            0px 120px 48px 0px rgba(0, 0, 0, 0.01),
            0px 187px 52px 0px rgba(0, 0, 0, 0);
    }

    .ddk-container .global-controls-bar-content {
        display: flex;
        flex-direction: row;
        height: 100%;
        width: 100%;
        padding-inline: var(--ddk-padding-xl, 48px);
        padding-top: var(--ddk-padding-sm, 16px);
        padding-bottom: var(--ddk-padding-md, 24px);
        gap: var(--ddk-grid-3);
        background: ${props =>
            props.theme.hero_controls_background_color ??
            'rgba(241, 241, 241, 0.90)'};
        border-bottom: 1px solid
            ${props =>
                props.theme.hero_controls_accent ??
                'var(--ddk-border-base-secondary, #C1C1C1)'};
        backdrop-filter: blur(7px);
        transition: all 0.3s ease-in-out;
    }

    .ddk-container .global-controls-bar-content.contols-header-hidden {
        height: 0%;
    }

    .ddk-container .global-controls-bar-controls-container {
        display: flex;
        width: 100%;
        flex-direction: column;
    }

    .ddk-container .global-controls-bar-controls.flex {
        display: flex;
        flex-direction: row;
        height: 100%;
        width: 100%;
        gap: var(--ddk-grid-3);
    }

    .ddk-container .global-controls-bar-controls.grid {
        display: grid;
        grid-template-columns: repeat(
            ${props => props.theme.hero_controls_grid_columns ?? 4},
            1fr
        );
        height: 100%;
        width: 100%;
        gap: var(--ddk-grid-3);
    }

    .ddk-container .global-controls-bar-secondary-drawer {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows 0.3s ease-in-out;
    }

    .ddk-container .global-controls-bar-secondary-drawer.open {
        grid-template-rows: 1fr;
    }

    .ddk-container .global-controls-bar-secondary-divider-wrapper {
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }

    .ddk-container .global-controls-bar-button-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-between;
        padding-top: 34px;
        color: ${props => props.theme.accent};
    }

    .ddk-container .global-controls-bar-item {
        display: flex;
        width: 100%;
        flex-direction: column;
        row-gap: 2px;
    }

    .ddk-container .global-controls-bar-item-label {
        display: flex;
        height: 32px;
        align-items: center;
        gap: var(--ddk-grid-half, 4px);
        align-self: stretch;
    }

    .ddk-container .global-controls-bar-item-label p {
        font-family: ${props => props.theme.font_family};
        font-size: ${props =>
            props.theme.hero_controls_label_font_size ??
            'var(--ddk-font-size-sm)'};
        font-weight: var(--ddk-font-semi-light);
        line-height: 20px;
        letter-spacing: 0.21px;
        color: ${props =>
            props.theme.hero_controls_label_text ??
            'var(--ddk-text-base-secondary)'};
    }

    .ddk-container .global-controls-bar-item-label > :is(i, svg) {
        color: ${props =>
            props.theme.hero_controls_label_text ??
            'var(--ddk-text-base-secondary)'};
        cursor: pointer;
    }

    .ddk-container .global-controls-bar-item-control {
        min-height: var(--ddk-grid-5);
        box-sizing: content-box;
    }

    .ddk-container .global-controls-bar-item-control > :first-child,
    .ddk-container
        .global-controls-bar-item-control
        > :first-child
        > :first-child {
        height: 100%;
    }

    /* WIP CSS for individual controls within global controls bar */

    .ddk-container
        .global-controls-bar-item-control
        input:not([type='checkbox'], [type='radio']),
    .ddk-container .global-controls-bar-item-control textarea,
    .ddk-container .global-controls-bar-item-control .rc-slider {
        height: 40px !important;
        width: 100%;
    }

    .ddk-container
        .global-controls-bar-item-control
        .dash-dropdown:focus-within,
    .ddk-container .global-controls-bar-item-control .dash-dropdown:hover {
        border: 1px solid ${props => props.theme.accent};
    }

    .ddk-container
        .global-controls-bar-item-control
        .Select--single
        .Select-value,
    .ddk-container
        .global-controls-bar-item-control
        .Select--single
        .Select-placeholder {
        margin-block: var(--ddk-grid-half);
        display: flex;
    }

    /* Temporary CSS Multi Select Dropdown in Controls Bar until we rework at component level */

    .ddk-container
        .global-controls-bar-item-control
        .Select-multi-value-wrapper {
        display: flex;
        flex-wrap: wrap;
        flex-direction: row;
        /* extra padding on the right to accommodate the dropdown arrow */
        padding-right: var(--ddk-grid-4);
    }

    .ddk-container
        .global-controls-bar-item-control
        .Select--multi
        .Select-value {
        margin: var(--ddk-grid-half);
        display: flex;
    }

    .ddk-container
        .global-controls-bar-item-control
        .Select--multi
        .Select-placeholder {
        line-height: var(--ddk-grid-5);
    }

    .ddk-container .global-controls-bar-item-control .dash-dropdown {
        border: 1px solid transparent;
        width: 100%;
    }

    .ddk-container .global-controls-bar-item-control .Select-control {
        width: 100%;
        overflow: visible;
    }

    .ddk-container .global-controls-bar-item-control .Select-arrow-zone {
        top: var(--ddk-grid-2);
        right: var(--ddk-grid-1);
    }

    .ddk-container .global-controls-bar-item-control .Select-clear-zone {
        display: none;
    }

    /* Range Input Overrides */
    .ddk-container .global-controls-bar-item-control input[type='range'] {
        box-sizing: border-box; /* prevent overflowing container */
        accent-color: var(--accent);
    }

    .ddk-container .global-controls-bar-item-control .DateRangePickerInput,
    .ddk-container .global-controls-bar-item-control .SingleDatePickerInput {
        display: flex;
        align-items: center;
        height: var(--ddk-grid-5);
    }

    .ddk-container .global-controls-bar-item-control .DateInput_input {
        padding-top: 8px;
    }

    /* Date Input Overrides */
    /* The immediate parent of the date picker has inline styles which give it a
    relative position and inline-block display. We need to override these.  */
    /* This selector targets just the immediate parent of the date picker */
    .ddk-container .global-controls-bar-item-control *:has(> .SingleDatePicker),
    .ddk-container .global-controls-bar-item-control *:has(> .DateRangePicker) {
        position: static !important;
        display: block !important;
    }

    /* Add a border to the date range input */
    .ddk-container .global-controls-bar-item-control .DateRangePicker {
        /* We don't have a value for control_border.borderStyle, so I'm reusing the global border style here for now. */
        border-width: ${({theme}) => theme.control_border.width};
        border-style: ${({theme}) => theme.border_style.borderStyle};
        border-color: ${({theme}) => theme.control_border.color};
    }

    /* Allow date range inputs to take up the parent's full width */
    .ddk-container .global-controls-bar-item-control .DateInput {
        width: 100%;
    }

    /* Border for checkbox and radio controls */
    .ddk-container .global-controls-bar-item-control input[type='checkbox'],
    .ddk-container .global-controls-bar-item-control input[type='radio'] {
        border: 1px solid
            ${props =>
                props.theme.body_text ??
                'var(--ddk-surface-base-inverse-surface4)'};
    }

    .global-controls-bar .divider {
        border: none;
        border-top: 1px solid
            ${props =>
                props.theme.hero_controls_accent ??
                'var(--ddk-border-base-secondary, #C1C1C1)'};
        margin: var(--ddk-grid-3) var(--ddk-grid-none);
    }

    .global-controls-bar-hide-button {
        position: absolute;
        bottom: -31px;
        right: 0;
        display: flex;
        z-index: 1;
        width: 48.6px;
        height: 32px;
        justify-content: center;
        align-items: center;
        background: ${props =>
            props.theme.hero_controls_background_color ??
            'rgba(241, 241, 241, 0.90)'};
        color: ${props =>
            props.theme.hero_controls_label_text ?? props.theme.text};
        backdrop-filter: blur(7px);
        border-radius: 0px 0px 0px 4px !important; /* Override bootstrap button radius */
        border-left: 1px solid ${props => props.theme.hero_controls_accent};
        border-bottom: 1px solid ${props => props.theme.hero_controls_accent};
        border-top: none;
        border-right: none;
        opacity: 0;
        transition:
            opacity 0.3s ease,
            box-shadow 0.7s ease;
    }

    .ddk-container .global-controls-bar-hide-button.stickied {
        opacity: 1;
        box-shadow:
            0px 7px 16px 0px rgba(0, 0, 0, 0.04),
            0px 30px 30px 0px rgba(0, 0, 0, 0.04),
            0px 67px 40px 0px rgba(0, 0, 0, 0.02),
            0px 120px 48px 0px rgba(0, 0, 0, 0.01),
            0px 187px 52px 0px rgba(0, 0, 0, 0);
    }

    .global-controls-bar-hide-button:hover {
        color: ${props => props.theme.hero_title_text};
        box-sizing: border-box;
        background: ${props =>
            getSimilarColor(
                chroma.valid(props.theme.hero_controls_background_color)
                    ? props.theme.hero_controls_background_color
                    : 'rgba(241, 241, 241, 0.90)',
                'darken',
                10
            )};
    }

    /* Media queries and CSS for mobile devices */

    @media screen and (max-width: 480px) {
        .ddk-container .global-controls-bar-button-wrapper button {
            width: 100%;
        }

        .ddk-container .global-controls-bar-content {
            flex-direction: column-reverse;
            height: 300px;
            overflow: scroll;
        }

        .ddk-container .global-controls-bar-controls.flex {
            flex-direction: column;
        }

        .ddk-container .global-controls-bar-controls.grid {
            grid-template-columns: 1fr;
        }
    }
`;

export default styles;
